import { API_BASE_URL } from "@/config";
import Swal from "sweetalert2";

export default {
  name: "UsageComponent",
  props: {
    service_id: {
      type: String,
      required: true,
    },
    client_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      billing_logs: [],
      filter_date_from: "",
      filter_date_to: "",
      filter_status: "",
      filter_billing_type: "",
      filter_source: "",
      usage_stats: {
        total_requests: 0,
        total_revenue: 0,
        total_cost: 0,
        profit_margin: 0,
        success_rate: 0,
      },

      logs_current_page: 1,
      logs_items_per_page: 10,
      _original_logs: null,
    };
  },
  computed: {
    ApexCharts() {
      return window.ApexCharts || null;
    },
    unique_statuses() {
      return [...new Set(this.billing_logs.map((log) => log.status))];
    },
    unique_billing_types() {
      return [
        ...new Set(
          this.billing_logs.map((log) => log.billing_type.billing_name)
        ),
      ];
    },
    unique_sources() {
      return [...new Set(this.billing_logs.map((log) => log.source))];
    },
    paginated_billing_logs() {
      const start = (this.logs_current_page - 1) * this.logs_items_per_page;
      const end = start + this.logs_items_per_page;
      return this.billing_logs.slice(start, end);
    },
    logs_total_pages() {
      return Math.ceil(this.billing_logs.length / this.logs_items_per_page);
    },
  },
  async mounted() {
    const load_script = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          const check = () => {
            if (src.includes("apexcharts") && window.ApexCharts) {
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          return check();
        }
        const s = document.createElement("script");
        s.src = src;
        s.async = false;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    await load_script("https://cdn.jsdelivr.net/npm/apexcharts");
    this.fetch_usage_logs();
  },
  methods: {
    fetch_usage_logs() {
      const TOKEN = localStorage.getItem("token");
      Swal.fire({
        title: "Loading Usage Logs",
        text: "Please wait while we fetch the usage logs.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        willClose: () => {
          Swal.hideLoading();
        },
      });

      fetch(
        `${API_BASE_URL}/verify_api/instant_verify_api/billing_logs/filter/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${TOKEN}`,
          },
          body: JSON.stringify({
            service_id: this.service_id,
            client_id: this.client_id,
          }),
        }
      )
        .then((response) => response.json())
        .then((data) => {
          Swal.close();
          this.billing_logs = data;
          this._original_logs = [...data];
          this.process_usage_data();
        })
        .catch(() => {
          Swal.close();
          Swal.fire({
            title: "Error",
            text: "Failed to fetch usage logs. Please try again.",
            icon: "error",
          });
        });
    },
    // Calculate overall usage statistics
    calculate_usage_stats() {
      const successful_requests = this.billing_logs.filter(
        (log) => log.status === "success"
      ).length;
      this.usage_stats = {
        total_requests: this.billing_logs.length,
        total_revenue: this.billing_logs.reduce(
          (sum, log) => sum + parseFloat(log.client_price),
          0
        ),
        total_cost: this.billing_logs.reduce(
          (sum, log) => sum + parseFloat(log.service_cost),
          0
        ),
        successful_requests,
      };

      this.usage_stats.success_rate = (
        (successful_requests / this.billing_logs.length) *
        100
      ).toFixed(2);
      this.usage_stats.profit_margin = (
        ((this.usage_stats.total_revenue - this.usage_stats.total_cost) /
          this.usage_stats.total_revenue) *
        100
      ).toFixed(2);
    },

    // Create overall usage timeline chart with ApexCharts
    create_overall_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#overall-usage-chart");
      if (container) container.innerHTML = "";

      const date_groups = this.group_by(this.billing_logs, (log) =>
        new Date(log.date_created).toLocaleDateString()
      );

      const dates = Object.keys(date_groups).sort();
      const success_data = dates.map(
        (date) =>
          date_groups[date].filter((log) => log.status === "success").length
      );
      const error_data = dates.map(
        (date) =>
          date_groups[date].filter((log) => log.status === "error").length
      );

      const options = {
        chart: {
          type: "area",
          height: 400,
          stacked: true,
          zoom: { enabled: true },
        },
        series: [
          {
            name: "Success",
            data: success_data,
            color: "#4BC0C0",
          },
          {
            name: "Error",
            data: error_data,
            color: "#FF6384",
          },
        ],
        xaxis: {
          categories: dates,
          title: { text: "Date" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        fill: {
          type: "solid",
          opacity: 0.7,
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        legend: {
          position: "top",
        },
        dataLabels: {
          enabled: false,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create workflow usage chart
    create_workflow_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#workflow-usage-chart");
      if (container) container.innerHTML = "";

      const workflow_groups = this.group_by(
        this.billing_logs.filter((log) => log.workflow),
        (log) => log.workflow.workflow_name
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Requests",
            data: Object.values(workflow_groups).map((group) => group.length),
          },
        ],
        xaxis: {
          categories: Object.keys(workflow_groups),
          title: { text: "Workflow Name" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        colors: ["#008FFB"],
        dataLabels: {
          enabled: true,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create status distribution pie chart
    create_status_distribution_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#status-distribution-chart");
      if (container) container.innerHTML = "";

      const status_groups = this.group_by(
        this.billing_logs,
        (log) => log.status
      );

      const options = {
        chart: {
          type: "pie",
          height: 350,
        },
        series: Object.values(status_groups).map((group) => group.length),
        labels: Object.keys(status_groups),
        colors: ["#4BC0C0", "#FF6384", "#FFCD56", "#C9CBCF", "#FF9F40"],
        legend: {
          position: "bottom",
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + "%";
          },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create hourly usage chart
    create_hourly_usage_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#hourly-usage-chart");
      if (container) container.innerHTML = "";

      const hourly_groups = this.group_by(this.billing_logs, (log) =>
        new Date(log.date_created).getHours()
      );
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const hourly_data = hours.map(
        (hour) => (hourly_groups[hour] || []).length
      );

      const options = {
        chart: {
          type: "bar",
          height: 350,
        },
        series: [
          {
            name: "Requests",
            data: hourly_data,
          },
        ],
        xaxis: {
          categories: hours,
          title: { text: "Hour of Day" },
        },
        yaxis: {
          title: { text: "Number of Requests" },
        },
        colors: ["#FEB019"],
        dataLabels: {
          enabled: false,
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create billing type distribution pie chart
    create_billing_type_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#billing-type-chart");
      if (container) container.innerHTML = "";

      const billing_groups = this.group_by(
        this.billing_logs,
        (log) => log.billing_type.billing_name
      );

      const options = {
        chart: {
          type: "pie",
          height: 350,
        },
        series: Object.values(billing_groups).map((group) => group.length),
        labels: Object.keys(billing_groups),
        colors: ["#4BC0C0", "#FF6384", "#FFCD56", "#C9CBCF", "#FF9F40"],
        legend: {
          position: "bottom",
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + "%";
          },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Create revenue vs cost scatter plot
    create_revenue_cost_chart() {
      if (!this.ApexCharts) return;

      const container = document.querySelector("#revenue-cost-chart");
      if (container) container.innerHTML = "";

      const options = {
        chart: {
          type: "scatter",
          height: 350,
          zoom: { enabled: true },
        },
        series: [
          {
            name: "Revenue",
            data: this.billing_logs.map((log) => ({
              x: new Date(log.date_created).getTime(),
              y: parseFloat(log.client_price),
            })),
          },
          {
            name: "Cost",
            data: this.billing_logs.map((log) => ({
              x: new Date(log.date_created).getTime(),
              y: parseFloat(log.service_cost),
            })),
          },
        ],
        xaxis: {
          type: "datetime",
          title: { text: "Date" },
        },
        yaxis: {
          title: { text: "Amount (R)" },
          labels: {
            formatter: function (val) {
              return "R" + val.toFixed(2);
            },
          },
        },
        colors: ["#008FFB", "#FF6384"],
        markers: {
          size: 6,
          hover: { size: 8 },
        },
      };

      const chart = new this.ApexCharts(container, options);
      chart.render();
    },

    // Process all data and create charts
    process_usage_data() {
      this.calculate_usage_stats();

      this.$nextTick(() => {
        this.create_overall_usage_chart();
        this.create_workflow_usage_chart();
        this.create_status_distribution_chart();
        this.create_hourly_usage_chart();
        this.create_billing_type_chart();
        this.create_revenue_cost_chart();
      });
    },
    group_by(array, key) {
      return array.reduce((result, item) => {
        const k = typeof key === "function" ? key(item) : item[key];
        if (!result[k]) result[k] = [];
        result[k].push(item);
        return result;
      }, {});
    },
    apply_filters() {
      if (!this._original_logs) {
        this._original_logs = [...this.billing_logs];
      }

      this.billing_logs = this._original_logs.filter((log) => {
        const date_match =
          (!this.filter_date_from ||
            new Date(log.date_created) >= new Date(this.filter_date_from)) &&
          (!this.filter_date_to ||
            new Date(log.date_created) <= new Date(this.filter_date_to));
        const status_match =
          !this.filter_status || log.status === this.filter_status;
        const billing_match =
          !this.filter_billing_type ||
          log.billing_type.billing_name === this.filter_billing_type;
        const source_match =
          !this.filter_source || log.source === this.filter_source;

        return date_match && status_match && billing_match && source_match;
      });

      this.logs_current_page = 1;
      this.process_usage_data();
    },
    reset_filters() {
      this.filter_date_from = "";
      this.filter_date_to = "";
      this.filter_status = "";
      this.filter_billing_type = "";
      this.filter_source = "";

      if (this._original_logs) {
        this.billing_logs = [...this._original_logs];
      }

      this.logs_current_page = 1;
      this.process_usage_data();
    },
    next_logs_page() {
      if (this.logs_current_page < this.logs_total_pages) {
        this.logs_current_page++;
      }
    },
    previous_logs_page() {
      if (this.logs_current_page > 1) {
        this.logs_current_page--;
      }
    },
  },
  beforeUnmount() {
    const chart_ids = [
      "#overall-usage-chart",
      "#workflow-usage-chart",
      "#status-distribution-chart",
      "#hourly-usage-chart",
      "#billing-type-chart",
      "#revenue-cost-chart",
    ];
    chart_ids.forEach((id) => {
      const container = document.querySelector(id);
      if (container) container.innerHTML = "";
    });
    // Clean up any dynamically loaded scripts if necessary
    const scripts = ["https://cdn.jsdelivr.net/npm/apexcharts"];
    scripts.forEach((src) => {
      const script = document.querySelector(`script[src="${src}"]`);
      if (script) {
        script.remove();
      }
    });
  },
};
